import * as React from 'react';

import {CardWidget} from 'shells/card/card-widget/card-widget';
import {GoalRow} from 'shells/row/goal-row/goal-row';

import type {LeadGoalFragment_goalConditions as GoalFragment} from 'nutshell-graphql-types';

import {useGetClickToCallProps} from '../../../core-entity/hooks/use-get-click-to-call-props';

import {useLeadCustomFieldHandlers} from '../../shared/hooks';

import {useGetGoalsByStage} from './graphql/hooks/use-get-goals-by-stage';

type Props = {
    leadId: string;
    selectedStageId: string;
};

export function GoalSection(props: Props) {
    const [error, setError] = React.useState<string | null | undefined>(null);

    const {goalsByStage, isLoading: isLoadingGoals} = useGetGoalsByStage(props.leadId, false);

    const activeGoalsByStage =
        goalsByStage && goalsByStage.find((stage) => stage.node.id === props.selectedStageId);
    const activeGoalConditions = (activeGoalsByStage && activeGoalsByStage.goalConditions) || [];

    const {
        handleTextFieldUpdate,
        handleEnumUpdate,
        handleCurrencyUpdate,
        handleAddressUpdate,
        handlePhoneUpdate,
        fieldErrors,
        setErrorForField,
    } = useLeadCustomFieldHandlers(props.leadId);
    const clickToCallProps = useGetClickToCallProps(props.leadId);

    const getErrorForCustomFields = React.useCallback(
        (goalCondition: GoalFragment): string | null | undefined => {
            const customField = goalCondition.conditionCustomField;
            if (customField && customField.node) {
                const fieldError = fieldErrors.find(
                    (e) => e.fieldName === customField.node.attribute.name
                );

                return fieldError ? fieldError.error : null;
            }

            return null;
        },
        [fieldErrors]
    );

    // We do not want to render anything if there are no goal conditions for the
    // selected stage
    if (activeGoalConditions.length === 0) {
        return null;
    }

    return (
        <CardWidget
            title='Goals'
            isLoading={isLoadingGoals}
            errorProps={{
                error,
                setError,
            }}
            hasBorder={false}
        >
            {activeGoalConditions.map((goalCondition) => (
                <GoalRow
                    key={goalCondition.condition.id}
                    goalCondition={goalCondition}
                    handleTextFieldUpdate={handleTextFieldUpdate}
                    handleEnumUpdate={handleEnumUpdate}
                    handleCurrencyUpdate={handleCurrencyUpdate}
                    handleAddressUpdate={handleAddressUpdate}
                    handlePhoneUpdate={handlePhoneUpdate}
                    error={getErrorForCustomFields(goalCondition)}
                    setErrorForField={setErrorForField}
                    clickToCallProps={clickToCallProps}
                />
            ))}
        </CardWidget>
    );
}
